import { Entity } from '@backstage/catalog-model';

const DEPLOY_SOURCE_ANNOTATION = 'rescale.com/deploy-source';
const APP_SOURCE_ANNOTATION = 'rescale.com/app-source';
const ARGOCD_TARGET_REVISION_ANNOTATION = 'rescale.com/argocd-target-revision';

export interface SourceLink {
  /** Human-readable shorthand, e.g. "rescale/argocd/megalodon/rescale-mcp-internal". */
  label: string;
  /** GitHub URL to the directory on the relevant branch. */
  url: string;
}

function buildGithubLink(value: string, branch: string): SourceLink | null {
  const segments = value.split('/').filter(Boolean);
  if (segments.length < 2) return null;

  const [org, repo, ...pathParts] = segments;
  const path = pathParts.join('/');
  const suffix = path ? `/tree/${branch}/${path}` : `/tree/${branch}`;

  return {
    label: value,
    url: `https://github.com/${org}/${repo}${suffix}`,
  };
}

/** Where the service is deployed/provisioned from (ArgoCD manifests or Terraform dir). */
export function getDeploySourceFromEntity(entity: Entity): SourceLink | null {
  const value = entity.metadata.annotations?.[DEPLOY_SOURCE_ANNOTATION];
  if (!value) return null;
  if (value.split('/').filter(Boolean).length < 3) return null;

  const branch =
    entity.metadata.annotations?.[ARGOCD_TARGET_REVISION_ANNOTATION] || 'main';
  return buildGithubLink(value, branch);
}

/**
 * Where the application's source code lives. Always a full URL — linked
 * verbatim — so any repo host (open-source, GitLab, a feature branch, a
 * monorepo subpath) is supported without hidden org/branch assumptions.
 */
export function getAppSourceFromEntity(entity: Entity): SourceLink | null {
  const value = entity.metadata.annotations?.[APP_SOURCE_ANNOTATION];
  if (!value) return null;
  if (!/^https?:\/\//i.test(value)) return null;
  return { label: value, url: value };
}
